import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { type Editor, type TLShape } from "tldraw";
import api from "@/utils/axios-utils";
import { ClosedCanvas } from "../components/ClosedCanvas";
import SpaceTopBar from "../components/SpaceTopBar";
import "@/canvas/canvas.css";

type SpaceCamera = ReturnType<Editor["getCamera"]>;

type TeacherViewWorkData = {
  initialSnapshot: Record<string, unknown> | null;
  personalShapes: TLShape[];
  personalCamera: { x: number; y: number; z: number } | null;
  studentName: string | null;
  timeSpentMs: number;
  spaceName?: string | null;
};

type ViewState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; data: TeacherViewWorkData };

export default function TeacherViewWorkPage() {
  const { spaceId, userId } = useParams<{ spaceId: string; userId: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<ViewState>({ status: "loading" });
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!spaceId || !userId) return;
    let cancelled = false;
    // Teacher-only endpoint — students receive their own snapshot via /join.
    const fetchSnapshot = (attempt: number) => {
      api
        .get<TeacherViewWorkData>(
          `/spaces/${spaceId}/student-recap-snapshot/${userId}`,
        )
        .then((res) => {
          if (cancelled) return;
          setState({ status: "ready", data: res.data });
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("[TeacherViewWorkPage] snapshot fetch failed", err);
          if (attempt < 2) {
            retryTimerRef.current = setTimeout(
              () => fetchSnapshot(attempt + 1),
              1500,
            );
          } else {
            setState({ status: "error" });
          }
        });
    };
    fetchSnapshot(1);
    return () => {
      cancelled = true;
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [spaceId, userId]);

  const data = state.status === "ready" ? state.data : null;

  return (
    <div className="h-[100dvh] flex flex-col bg-white font-mulish">
      <SpaceTopBar
        spaceStatus="closed"
        spaceIdOverride={spaceId}
        spaceNameOverride={data?.spaceName ?? null}
        watchMode="recap"
        watchingStudentId={Number(userId)}
        watchingStudentName={data?.studentName ?? null}
        watchingTimeSpentMs={data?.timeSpentMs ?? null}
        onStopWatching={() => navigate(`/spaces/${spaceId}/recap`)}
      />

      <div className="flex-1 relative">
        {state.status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full border-2 border-lightgreen border-t-transparent animate-spin" />
          </div>
        )}
        {state.status === "error" && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-sm text-red-400 font-mulish">
              Failed to load snapshot. Please refresh.
            </p>
          </div>
        )}
        {state.status === "ready" && (
          <ClosedCanvas
            snapshot={state.data.initialSnapshot}
            personalShapes={state.data.personalShapes}
            camera={state.data.personalCamera as SpaceCamera}
          />
        )}
      </div>
    </div>
  );
}
